/**
 * @voltxampere/components — Diode (nonlinear placeholder)
 *
 * Sprint 4 ships the definition skeleton with the bare minimum
 * Shockley parameters; Sprint 7+ will add temperature dependence,
 * reverse breakdown, and capacitance modeling. The `nonlinear: true`
 * flag tells Sprint 5's DC solver to route this through Newton-
 * Raphson rather than the linear stamp path.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const DiodeParamsSchema = z
    .object({
    /** Saturation current `Is` in amps; default ~ 1e-14 A for a typical Si signal diode. */
    saturationCurrent: z.number().positive().finite().default(1e-14),
    /** Ideality factor `n`; default 1 (ideal Shockley). */
    ideality: z.number().positive().finite().default(1),
    /** Optional series resistance for non-ideal diodes. */
    seriesResistance: z.number().nonnegative().finite().optional(),
    /**
     * Sprint 22 D2 / D-22-6 — junction capacitance parameters
     * (zero-default, additive — Sprint 5/6/7 fixtures stay
     * exactly zero-diff).
     *
     *   Cj0:  zero-bias junction capacitance (F)
     *   Vj:   built-in junction voltage     (V)
     *   M:    grading coefficient            (—)   typically 0.33 (linear) … 0.5 (abrupt)
     *   Tt:   forward-transit time          (s)   diffusion-cap parameter
     *   FC:   forward-bias depletion-cap    (—)   linearisation knee, default 0.5
     *
     * Total small-signal AC capacitance:
     *   C_total(Vd) = C_depletion(Vd) + C_diffusion(Vd)
     *               = Cj0 / (1 - Vd/Vj)^M  +  gd · Tt
     * where `gd` is the existing Sprint 20 small-signal slope.
     *
     * Forward-bias linearisation per SPICE3 (FC·Vj knee) keeps
     * (1 - Vd/Vj)^M from blowing up as Vd → Vj.
     */
    Cj0: z.number().nonnegative().finite().optional(),
    Vj: z.number().positive().finite().optional(),
    M: z.number().positive().finite().optional(),
    Tt: z.number().nonnegative().finite().optional(),
    FC: z.number().min(0).lt(1).optional(),
})
    .strict();
export const diodeDefinition = {
    type: "diode",
    category: "active",
    displayName: { tr: "Diyot", en: "Diode" },
    symbol: "D",
    pins: [
        { id: "anode", side: "left", offset: 0, label: "A" },
        { id: "cathode", side: "right", offset: 0, label: "K" },
    ],
    parameterSchema: DiodeParamsSchema,
    nonlinear: true,
};
//# sourceMappingURL=diode.js.map